"use client";

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useState } from "react";
import { Brain, Bot, Briefcase, ShieldCheck, Rocket, Search } from "lucide-react";
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogSubtitle,
  DialogCloseButton,
  DialogFooter,
} from "@/components/ui/dialog";
import { PrimaryButton } from "@/components/ui/primary-button";
import { Badge } from "@/components/ui/badge";
import { TopicId } from "@/lib/constants";

export const icons: Record<TopicId, typeof Briefcase> = {
  ai_strategy: Briefcase,
  ai_agents: Bot,
  n8n_automation: Brain,
  responsible_ai: ShieldCheck,
  geo_seo: Search,
  ai_future: Rocket,
};

export function TopicCard({ topicId }: { topicId: TopicId }) {
  const t = useTranslations('TopicsSection.topics.' + topicId);
  const tCommon = useTranslations('TopicsSection');
  const [open, setOpen] = useState(false);
  const Icon = icons[topicId];

  // Get the arrays with proper typing
  const tags = t.raw('tags') as string[];
  const details = t.raw('details') as string[];

  return (
    <Card className="border-border rounded-2xl break-words hyphens-auto flex flex-col h-full p-6 transition-all duration-300 glass relative overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-4 p-0 pb-1 relative z-10">
        <div className="rounded-md bg-primary/10 p-2">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold text-gradient-brand line-clamp-2">{t('title')}</CardTitle>
      </CardHeader>

      <CardContent className="p-0 pt-2 text-base leading-relaxed text-muted-foreground flex-grow flex flex-col relative z-10">
        <div className="flex flex-col gap-4">
          <p className="text-lg text-foreground text-justify break-words hyphens-auto line-clamp-4 lg:text-left">{t('summary')}</p>
        </div>
        <div className="flex flex-wrap gap-2 relative mt-6">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="bg-secondary text-muted-foreground border border-primary/80 hover:bg-primary/20 transition-colors">
              {tag}
            </Badge>
          ))}
        </div>
      </CardContent>

      <CardFooter className="flex justify-center p-0 pt-6 mt-auto">
        <Dialog
          open={open}
          onOpenChange={setOpen}
          aria-label={`Details about ${t('title')}`}
        >
          <DialogTrigger asChild>
            <PrimaryButton>{tCommon('learnMore')}</PrimaryButton>
          </DialogTrigger>
          <DialogContent className="xl:!max-w-[900px] max-h-[85vh] overflow-y-auto glass-strong border-primary/20">
            <DialogHeader className="space-y-4 pb-6">
              <DialogTitle className="text-3xl md:text-4xl font-bold text-gradient-brand">{t('title')}</DialogTitle>
              <DialogSubtitle className="text-sm md:text-base text-primary/80 font-medium uppercase tracking-wide">{t('targetGroup')}</DialogSubtitle>
              <DialogDescription className="space-y-4">
                <p className="text-base md:text-lg text-foreground leading-relaxed font-medium">{t('summary')}</p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{t('description')}</p>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-6">
              <div>
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-foreground">{tCommon('keyPoints')}</h3>
                <ul className="space-y-3 text-sm md:text-base">
                  {details.map((detail, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-muted-foreground leading-relaxed">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 py-4 border-t border-border">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="bg-primary/10 text-primary border border-primary/30 px-3 py-1.5 text-xs md:text-sm font-medium">
                  {tag}
                </Badge>
              ))}
            </div>
            <DialogFooter className="gap-3 pt-6 border-t border-border">
              <Button asChild className="flex-1 md:flex-none hover:scale-[1.02] transition-all">
                <Link
                  href="/#book_now"
                  scroll={false}
                  onClick={() => {
                    setOpen(false);
                    // Use setTimeout to ensure the dialog is closed before scrolling
                    setTimeout(() => {
                      const element = document.getElementById('book_now');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }}
                >
                  {tCommon('book_course')}
                </Link>
              </Button>
              <DialogCloseButton className='bg-secondary/50 text-foreground hover:bg-secondary border border-border' />
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  );
}
